/*
Description: This is the file that will handle interacting with the Practice Pro EMR (app.ptpracticepro.com)
Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

export function getPageInfo() {
  // function getPageInfo() {
  /*
          Description: This function will get the page info from the note
          Return: A dictionary with the page info
              {
                  "URL": Base URL for the page,
                  "Note Type": "PT Daily/SOAP Note",...
              }
          */
  const baseURL = window.location.origin;
  let noteType;

  // Therabill: note type comes from the currently selected option in this chooser.
  // Example:
  //   <select id="POCFORMCHOOSER">
  //     <option>Daily Note</option>
  //     <option>Initial Evaluation</option>
  //     ...
  //   </select>
  const formChooser = querySelectorDeep(
    '#POCFORMCHOOSER'
  ) as HTMLSelectElement | null;
  if (formChooser && formChooser.selectedIndex >= 0) {
    const selected = formChooser.options[formChooser.selectedIndex];
    noteType = selected?.textContent?.trim() || selected?.label?.trim();
  }

  // Header title (e.g. "New Session Note") is useful context, but user confirmed the
  // selected form chooser option is the canonical note type, so treat header as fallback.
  if (!noteType) {
    noteType =
      document.querySelector('#POCFORMTITLESPAN_HEADER')?.textContent?.trim() ||
      noteType;
  }

  if (noteType) {
    return {
      URL: baseURL,
      'Note Type': noteType
    };
  }

  let parentElement = document.querySelector(
    '.k-dropdown-wrap.k-state-default'
  );
  if (parentElement) {
    let spanElements = parentElement.querySelectorAll('span.k-state-default');
    if (spanElements.length > 0) {
      let firstSpanElement = spanElements[0];

      // Check if the first span element has only the class "k-state-default"
      if (
        firstSpanElement.classList.length === 1 &&
        firstSpanElement.classList.contains('k-state-default')
      ) {
        noteType = firstSpanElement?.textContent?.trim();
      }
    }
  }
  return {
    URL: baseURL,
    'Note Type': noteType || 'Default Note'
  };
}

export async function getPatientInfo() {
  // function getPatientInfo() {
  /*
          Description: This function will get the patient info from the note
          Return: A dictionary with the patient info
          {
              "EMR ID": 0x1234 or "michalskij"
              "First Name":firstName,
              "Last Name": lastName,
              "DOB": dob,
          }
          */
  // Therabill can show patient name in multiple places depending on the view.
  // Example note header:
  //   <span id="NOTECLIENTNAME">Bruce Wayne</span>
  // Fallback avatar:
  //   #pat-avatar-name (often includes pronouns / EMR ID metadata)
  // Wait briefly for note header to appear (Therabill can load it after initial paint)
  let noteClientNameEl = querySelectorDeep('#NOTECLIENTNAME');
  if (!noteClientNameEl) {
    for (let i = 0; i < 10 && !noteClientNameEl; i++) {
      await sleep(150);
      noteClientNameEl = querySelectorDeep('#NOTECLIENTNAME');
    }
  }

  const rawName = (noteClientNameEl?.textContent || '').trim();
  const tokens = rawName.split(/\s+/).filter(Boolean);
  const firstName = tokens[0] || '';
  const lastName = tokens.slice(1).join(' ') || '';
  const emrID = rawName.replace(/\s/g, '');
  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: '05/29/2023'
  };
}

/* 
  questions - This is where we will store the questions
            - a list of dictionaries
       [{
          "Question": "Where was the injury?", 
          "Type": "Multiple Choice", 
          "Choices": ["Back", "Neck", "Shoulder"]
       },
          ...
          ]
   
  */

/*
  question Interactions - This is where we will store the interactions for each question
                          - a dictionary of dictionaries
           {
              "Where was the injury?": {
                  "Type": "Multiple Choice",
                  "Choices"{
                      "Back": () => {click the back button},
                      "Neck": () => {click the neck button},
                      "Shoulder": () => {click the shoulder button}
                  }
              },
          },
          {
              "What is the injury?": {
                  "Type": "Short Answer",
                  "Input": (val) => {input the value into the input box}
          }
              ...
           }
  */
var questionInteractions = {};
var questions = [];

export async function getQuestions() {
  questions = [];
  questionInteractions = {};

  // Therabill Plan of Care / Session Note: only scrape narrative textareas.
  let pocContainer = querySelectorDeep<HTMLElement>('#POCFORM_CONTAINER');
  if (!pocContainer) {
    for (let i = 0; i < 10 && !pocContainer; i++) {
      await sleep(150);
      pocContainer = querySelectorDeep<HTMLElement>('#POCFORM_CONTAINER');
    }
  }
  if (pocContainer) {
    getPOCFormNarrativeQuestions(pocContainer as HTMLElement);
    Object.keys(questionInteractions).forEach((key) => {
      questions.push(questionInteractions[key]);
    });
    return questions;
  }
}

function getPOCFormNarrativeQuestions(pocContainer: HTMLElement) {
  // Example structure:
  //  <div id="FIELDOVERDIV_0"> ... <td class="blackFontM">Subjective</td> ... <textarea id="NARRATIVEENTRY_0"></textarea>
  const textareas = pocContainer.querySelectorAll(
    'textarea[id^="NARRATIVEENTRY_"]'
  );

  textareas.forEach((ta) => {
    const textarea = ta as HTMLTextAreaElement;
    const fieldWrapper = textarea.closest(
      'div[id^="FIELDOVERDIV_"]'
    ) as HTMLElement | null;
    const heading =
      fieldWrapper?.querySelector('td.blackFontM')?.textContent?.trim() || '';

    if (!heading) return;

    if (!questionInteractions[heading]) {
      questionInteractions[heading] = {
        Type: 'Long Answer',
        Question: heading,
        Key: heading,
        ExistingNote: (textarea.value || '').trim(),
        Input: async (val, replyType) => {
          await answerLongAnswer(textarea, val, replyType);
        }
      };
    }
  });
}

// --- Therabill helpers: search same-origin iframes + wait briefly for dynamic pages ---
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function getSameOriginFrameDocuments(rootDoc: Document = document): Document[] {
  const docs: Document[] = [];
  try {
    const iframes = Array.from(
      rootDoc.querySelectorAll('iframe')
    ) as HTMLIFrameElement[];
    for (const frame of iframes) {
      try {
        const d = frame.contentDocument;
        if (d) docs.push(d);
      } catch {
        // cross-origin, ignore
      }
    }
  } catch {
    // ignore
  }
  return docs;
}

function querySelectorDeep<T extends Element = Element>(
  selector: string
): T | null {
  const direct = document.querySelector(selector) as T | null;
  if (direct) return direct;
  for (const d of getSameOriginFrameDocuments()) {
    const found = d.querySelector(selector) as T | null;
    if (found) return found;
  }
  return null;
}

async function answerLongAnswer(localSubQuestionTextArea, val, replyType) {
  try {
    if (isEmptyOrNull(val)) {
      return;
    }
    // Actually Type
    if (replyType === 'append') {
      setReactTextareaValue(
        localSubQuestionTextArea,
        localSubQuestionTextArea.value + '\n' + val
      );
    } else if (replyType === 'overwrite') {
      setReactTextareaValue(localSubQuestionTextArea, val);
    }
  } catch (error) {
    getLogFromInjector();
  }
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

async function setReactTextareaValue(editorElement, value) {
  // Create and dispatch a click event
  const clickEvent = new Event('click', { bubbles: true });
  editorElement.dispatchEvent(clickEvent);

  // Create and dispatch a focus event
  const focusEvent = new Event('focus', { bubbles: true });
  editorElement.dispatchEvent(focusEvent);

  // Create and dispatch a focusin event
  const focusinEvent = new Event('focusin', { bubbles: true });
  editorElement.dispatchEvent(focusinEvent);

  // Update the value
  editorElement.value = value;

  // Dispatch an input event (many apps listen for this rather than change)
  const inputEvent = new Event('input', { bubbles: true });
  editorElement.dispatchEvent(inputEvent);

  // Create and dispatch a change event
  const changeEvent = new Event('change', { bubbles: true });
  editorElement.dispatchEvent(changeEvent);

  // Create and dispatch a blur event
  const blurEvent = new Event('blur', { bubbles: true });
  editorElement.dispatchEvent(blurEvent);

  // Create and dispatch a focusout event
  const focusoutEvent = new Event('focusout', { bubbles: true });
  editorElement.dispatchEvent(focusoutEvent);

  // Create and dispatch a submit event
  const submitEvent = new Event('submit', { bubbles: true });
  editorElement.dispatchEvent(submitEvent);
}

export function answerQuestions(answers) {
  // function answerQuestions(answers) {
  /*
          Description: This function will take the answers and populate the questions
          
          Args: 
              answers: A dictionary of answers
                  "Question": "The question in question"
                  "Answer": "The answer to the question"
         
          Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
              [
                  {
                      Question: "Where was the injury?", 
                      Answer: "Back" 
                      Error: "The error message" (Choice not found, etc)
                  },
                  ...
              }
      
          */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(answer['Question'], answer['Answer'], undefined);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export function answerQuestion(question, answer, replyType) {
  // function answerQuestion(question, answer) {
  /*
          Description: This function will take the answer and populate the question
          
          Args: 
              question: The question in question
              answer: The answer to the question
         
          Return: null if success otherwise a string with the error message
          */

  // console.log("Answering Question: '" + question + "' with '" + answer + "'");

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions);
    // console.log(Object.keys(questionInteractions));

    return 'Question not found';
  }

  // If it is a short answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.error('Question: ' + question);
      console.error('Answer:' + answer);
      console.error(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
